// Browser-side geo access. The Google key is server-only, so the SPA never talks to
// Google directly — it calls the `geo-geocode` edge function with the user's Supabase
// JWT, and the function holds the key. Nothing under `src/` may ever import the
// server-side google helper; this HTTP wrapper is the only bridge.
import { FUNCTIONS_URL, SUPABASE_PUBLISHABLE_KEY, supabase } from './supabaseClient'

export interface GeocodeResult {
  lat: number
  lng: number
  formatted_address: string
}

/**
 * Resolve a typed address to coordinates.
 * Returns null when the address has no match (404 `no_match`).
 * Throws on missing session, network failure, or a server error.
 */
export async function geocode(address: string): Promise<GeocodeResult | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session) throw new Error('Not signed in')

  const res = await fetch(`${FUNCTIONS_URL}/geo-geocode`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      apikey: SUPABASE_PUBLISHABLE_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ address }),
  })

  if (res.status === 404) return null
  if (!res.ok) {
    const error = await res
      .json()
      .then((b: unknown) => (b as { error?: string } | null)?.error)
      .catch(() => undefined)
    throw new Error(`Geocoding failed (${res.status}${error ? `: ${error}` : ''})`)
  }

  return (await res.json()) as GeocodeResult
}
